import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { useIsCurrentUserDeviationAuthor } from '../../../utils/hooks/useIsCurrentUserDeviationAuthor';
import { isBoostedDeviation } from '../../../DeviationViews/_helpers';
import ActionBarItemWrapper from '../../_partials/items/ActionBarItemWrapper';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import BoostIcon from '@wix/da-ds/pkg/Icons/24x24/Boost';
import BoostFilledIcon from '@wix/da-ds/pkg/Icons/24x24/BoostFilled';
import BoostRootModal from './modals/BoostRootModal';
import { BiData, GiveBadgeAwardBiEvent } from '@wix/da-bi/pkg/events';

interface Props {
  deviation: PapiDeviation;
  className?: string;
}

const BoostButton: React.FC<Props> = ({ deviation, className }) => {
  const { t } = useTranslation();
  const { hasCoreMembership, coreLevel } = useContext(SessionContext);
  const isOwnerView = useIsCurrentUserDeviationAuthor(deviation);
  const isBoosted = isBoostedDeviation(deviation);
  const [isShowingModal, setShowingModal] = useState<boolean>(false);
  const [shouldForceTooltipVisible, setShouldForceTooltipVisible] = useState<boolean>(false);
  const [hasHoveredOnce, setHasHoveredOnce] = useState<boolean>(false);

  const showModal = () => setShowingModal(true);
  const closeModal = () => setShowingModal(false);

  const hasEligibleCoreTier = hasCoreMembership && coreLevel !== 'core_access';
  const { freeBoostsRemaining = 0 } =
    deviation.extended?.boostRestrictions || {};

  const shouldShowDiscoverBoostTooltip =
    isOwnerView &&
    hasEligibleCoreTier &&
    freeBoostsRemaining > 0 &&
    !isBoosted;

  // Show discover boost tooltip on page load, hide on first hover
  useEffect(() => {
    if (shouldShowDiscoverBoostTooltip) {
      setShouldForceTooltipVisible(true);
    }
  }, [shouldShowDiscoverBoostTooltip]);

  const handleMouseEnter = () => {
    if (shouldShowDiscoverBoostTooltip && !hasHoveredOnce) {
      setHasHoveredOnce(true);
      setShouldForceTooltipVisible(false);
    }
  };

  return (
    <>
      <ActionBarItemWrapper
        className={className}
        tooltip={
          shouldShowDiscoverBoostTooltip
            ? t('boostDeviation.discoverTooltip', {
                count: freeBoostsRemaining,
              })
            : t(isBoosted ? 'actionbar.currentlyBoosted' : 'actionbar.boost')
        }
        forceVisibleTooltip={shouldForceTooltipVisible}
      >
        <IconButton
          disabled={isBoosted}
          size="medium"
          icon={isBoosted ? BoostFilledIcon : BoostIcon}
          onClick={showModal}
          onMouseEnter={handleMouseEnter}
          aria-label={
            isBoosted ? t('actionbar.currentlyBoosted') : t('actionbar.boost')
          }
          biData={BiData<GiveBadgeAwardBiEvent>({
            evid: 320,
            sectionname: 'action_bar',
            itemid: deviation.deviationId,
            typeid: deviation.typeId,
            badge_type: 'boost',
            badge_typeid: 176,
          })}
        />
      </ActionBarItemWrapper>
      {isShowingModal && (
        <BoostRootModal deviation={deviation} onRequestClose={closeModal} />
      )}
    </>
  );
};
BoostButton.displayName = 'BoostButton';

export default BoostButton;
